<template>
  <div>
    <p class="sw-intro">{{ t('Revise los datos antes de enviar. Puede corregir cualquier apartado.') }}</p>

    <dl class="sw-resumen">
      <div class="sw-resumen__fila">
        <dt>{{ t('Encargo') }}</dt>
        <dd>{{ tipo?.nombre }}</dd>
        <button type="button" class="sw-editar" @click="irAPaso('tipo')">{{ t('Editar') }}</button>
      </div>

      <div v-if="encargoPlantilla?.pasoDestino" class="sw-resumen__fila">
        <dt>{{ t('Notaría') }}</dt>
        <dd>
          {{ destino ? destino.nombre : t('Sin preferencia') }}
          <template v-if="destino?.proximaFecha && !estado.firmaVideoconferencia && textoFechaFirma(destino.proximaFecha)">
            <br><span class="sw-tenue">{{ t('Próxima fecha indicada por la notaría: {fecha} (orientativa)', { fecha: textoFechaFirma(destino.proximaFecha) }) }}</span>
          </template>
        </dd>
        <button type="button" class="sw-editar" @click="irAPaso('destino')">{{ t('Editar') }}</button>
      </div>

      <div v-if="esProfesional" class="sw-resumen__fila">
        <dt>{{ t('Presentada por') }}</dt>
        <dd>
          {{ pro.nombre }}<template v-if="pro.empresa"> · {{ pro.empresa }}</template><br>
          <span class="sw-tenue">{{ pro.telefono }} · {{ pro.email }}</span>
        </dd>
        <button type="button" class="sw-editar" @click="irAPaso('presentador')">{{ t('Editar') }}</button>
      </div>

      <div class="sw-resumen__fila">
        <dt>{{ esProfesional ? t('Cliente') : t('Sus datos') }}</dt>
        <dd>
          {{ nombrePersona(estado.solicitante) }}<br>
          <span class="sw-tenue">{{ detallePersona(estado.solicitante) }}</span>
        </dd>
        <button type="button" class="sw-editar" @click="irAPaso('solicitante')">{{ t('Editar') }}</button>
      </div>

      <div v-if="pasos.includes('intervinientes')" class="sw-resumen__fila">
        <dt>{{ t('Otros intervinientes') }}</dt>
        <dd>
          <template v-if="estado.intervinientes.length">
            <div v-for="p in estado.intervinientes" :key="p.uid">
              {{ nombrePersona(p) }} <span class="sw-tenue">· {{ detallePersona(p) }}</span>
            </div>
          </template>
          <span v-else class="sw-tenue">{{ t('Ninguno por ahora') }}</span>
        </dd>
        <button type="button" class="sw-editar" @click="irAPaso('intervinientes')">{{ t('Editar') }}</button>
      </div>

      <div v-if="tipo?.llevaBienes && (bien.descripcion || bien.referenciaCatastral)" class="sw-resumen__fila">
        <dt>{{ t('Bien') }}</dt>
        <dd>
          {{ bien.descripcion }}
          <span v-if="bien.referenciaCatastral" class="sw-tenue"><br>{{ t('Ref. catastral') }} {{ bien.referenciaCatastral }}</span>
        </dd>
        <button type="button" class="sw-editar" @click="irAPaso('detalle')">{{ t('Editar') }}</button>
      </div>

      <div v-if="adjuntos.length" class="sw-resumen__fila">
        <dt>{{ t('Documentación') }}</dt>
        <dd>
          <div v-for="a in adjuntos" :key="a.uid">{{ nombreDocumento(a.documento) }} <span class="sw-tenue">· {{ a.file.name }}</span></div>
        </dd>
        <button type="button" class="sw-editar" @click="irAPaso('detalle')">{{ t('Editar') }}</button>
      </div>

      <div v-if="cita" class="sw-resumen__fila">
        <dt>{{ t('Cita propuesta') }}</dt>
        <dd>{{ textoCita }} <span class="sw-tenue"><br>{{ t('(pendiente de confirmar por la notaría)') }}</span></dd>
        <button type="button" class="sw-editar" @click="irAPaso('detalle')">{{ t('Editar') }}</button>
      </div>

      <div v-if="estado.comentario" class="sw-resumen__fila">
        <dt>{{ t('Comentario') }}</dt>
        <dd class="sw-prewrap">{{ estado.comentario }}</dd>
        <button type="button" class="sw-editar" @click="irAPaso('detalle')">{{ t('Editar') }}</button>
      </div>

      <!-- Con paso de destino (minotaria) se muestra también "en persona": la modalidad se eligió junto a la notaría -->
      <div v-if="videoconferenciaEn(estado.tipoOperacion) && (estado.firmaVideoconferencia || encargoPlantilla?.pasoDestino)" class="sw-resumen__fila">
        <dt>{{ t('Firma') }}</dt>
        <dd>{{ estado.firmaVideoconferencia ? t('Por videoconferencia') : t('En persona, en la notaría') }}</dd>
        <button type="button" class="sw-editar" @click="irAPaso(preguntarFirma ? 'detalle' : 'destino')">{{ t('Editar') }}</button>
      </div>

      <div v-if="config.PermitirPresupuesto && estado.quierePresupuesto" class="sw-resumen__fila">
        <dt>{{ t('Presupuesto') }}</dt>
        <dd>{{ t('Quiere recibir un presupuesto antes de empezar') }}</dd>
        <button type="button" class="sw-editar" @click="irAPaso('detalle')">{{ t('Editar') }}</button>
      </div>
    </dl>

    <!-- Primera capa informativa RGPD -->
    <div class="sw-rgpd">
      <p>
        <strong>{{ t('Responsable:') }}</strong> {{ nombreNotaria }}.
        <strong>{{ t('Finalidad:') }}</strong>
        {{ conNV
          ? t('gestionar su encargo, darle de alta como cliente en la Notaría Virtual y contactarle para prepararlo.')
          : t('gestionar su encargo y contactarle para prepararlo.') }}
        <strong>{{ t('Legitimación:') }}</strong> {{ t('su consentimiento y la aplicación de medidas precontractuales.') }}
        <strong>{{ t('Derechos:') }}</strong> {{ t('acceso, rectificación, supresión y demás derechos ante la notaría.') }}
        <template v-if="esProfesional">
          {{ t('Al enviar los datos de terceros, declara contar con su autorización para facilitarlos.') }}
        </template>
      </p>
      <component :is="encargoPlantilla.avisoPrivacidad" v-if="encargoPlantilla?.avisoPrivacidad" />
      <v-checkbox v-model="estado.consentimiento.aceptado" hide-details="auto" density="compact"
        :error-messages="errores['consentimiento'] ? [errores['consentimiento']] : []">
        <template #label>
          <span>{{ t('He leído y acepto la') }}
            <NuxtLink to="/protecciondatos" target="_blank" @click.stop>{{ t('política de privacidad') }}</NuxtLink>
          </span>
        </template>
      </v-checkbox>
    </div>
  </div>
</template>

<script setup lang="ts">
import { t } from '../../composables/solicitudTextos'
import { computed } from 'vue'
import { useStore } from '../../store/index'
import type { Interviniente } from '../../types/solicitudWeb'
import { nombreTipoDocumento, useSolicitudWeb } from '../../composables/useSolicitudWeb'
import { textoFechaFirma } from '../../composables/useMinotaria'

const store = useStore()
const { estado, errores, adjuntos, tipo, pasos, esProfesional, conNV, config, irAPaso, nombreRol, nombreDocumento, encargoPlantilla, destino, reglasCita, citaValida, videoconferenciaEn, preguntarFirma } = useSolicitudWeb()
// Cita de firma (ref: https://trello.com/c/Bnl5pAM2)
const cita = computed(() => citaValida(estado.value.cita, reglasCita.value))
const textoCita = computed(() => {
  if (!cita.value) return ''
  const [a, m, d] = cita.value.fecha.split('-')
  return `${d}/${m}/${a}` + (cita.value.hora ? ' · ' + cita.value.hora : '')
})
const pro = computed(() => estado.value.presentador.profesional)
const bien = computed(() => estado.value.bienes[0])
const nombreNotaria = computed(() => destino.value?.nombre ?? store.NOTARIA?.Nombre ?? t('La notaría'))

function nombrePersona(p: Interviniente) {
  return p.tipoPersona === 'JURIDICA'
    ? t('{empresa} (representada por {nombre})', { empresa: p.razonSocial, nombre: `${p.representante.nombre} ${p.representante.apellidos}` })
    : `${p.nombre} ${p.apellidos}`
}

function detallePersona(p: Interviniente) {
  const partes = [
    `${nombreTipoDocumento(p.documento.tipo)} ${p.documento.numero}`,
    p.rol ? nombreRol(p.rol) : '',
    p.intervieneConyuge ? t('con cónyuge') : '',
    p.telefono,
    p.email
  ]
  return partes.filter(Boolean).join(' · ')
}
</script>
